import { useEffect, useMemo, useState } from 'react';
import { createResource,getResources,getResourceById, updateResource} from '../../../router/api';
import toast from 'react-hot-toast';

interface Invoice {
  id: number;
  numero: string;
  client: {
    id: number;
    raison_sociale: string;
    email: string;
  };
  total_ttc: number;
  montant_paye: number;
  date_echeance: string;
  statut_paiement: 'IMPAYEE' | 'PAYEE_PARTIEL' | 'PAYEE' | 'EN_LITIGE';
  litige?: { description: string } | null;
}

export default function CreditReporting() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInvoices = async () => {
      setLoading(true);
      try {
        const res = await getResources(`factures-client`);
        setInvoices(res.data.data || []);
      } catch (err) {
        toast.error('Erreur lors du chargement des données crédit');
      } finally {
        setLoading(false);
      }
    };
    fetchInvoices();
  }, []);

  // Calcul des statistiques de crédit
  const creditStats = useMemo(() => {
    const unpaidInvoices = invoices.filter(inv => inv.statut_paiement !== 'PAYEE');
    const overdueInvoices = unpaidInvoices.filter(inv => new Date(inv.date_echeance) < new Date());
    const disputeInvoices = unpaidInvoices.filter(inv => inv.litige);

    const totalOutstanding = unpaidInvoices.reduce((sum, inv) => sum + (inv.total_ttc - inv.montant_paye), 0);
    const totalOverdue = overdueInvoices.reduce((sum, inv) => sum + (inv.total_ttc - inv.montant_paye), 0);
    const totalCurrent = totalOutstanding - totalOverdue;
    const totalDispute = disputeInvoices.reduce((sum, inv) => sum + (inv.total_ttc - inv.montant_paye), 0);

    // Répartition par tranche de retard
    const agingBuckets = {
      current: 0,
      days1to30: 0,
      days31to60: 0,
      days61to90: 0,
      over90: 0,
    };

    unpaidInvoices.forEach(inv => {
      const amount = inv.total_ttc - inv.montant_paye;
      const daysOverdue = Math.max(0, Math.floor((new Date().getTime() - new Date(inv.date_echeance).getTime()) / (1000 * 3600 * 24)));

      if (daysOverdue === 0) agingBuckets.current += amount;
      else if (daysOverdue <= 30) agingBuckets.days1to30 += amount;
      else if (daysOverdue <= 60) agingBuckets.days31to60 += amount;
      else if (daysOverdue <= 90) agingBuckets.days61to90 += amount;
      else agingBuckets.over90 += amount;
    });

    const averagePaymentDelay = overdueInvoices.length > 0
      ? Math.round(overdueInvoices.reduce((sum, inv) => {
          const days = Math.max(0, (new Date().getTime() - new Date(inv.date_echeance).getTime()) / (1000 * 3600 * 24));
          return sum + days;
        }, 0) / overdueInvoices.length)
      : 0;

    return {
      totalOutstanding,
      totalCurrent,
      totalOverdue,
      totalDispute,
      agingBuckets,
      averagePaymentDelay,
      overdueCount: overdueInvoices.length,
      disputeCount: disputeInvoices.length,
    };
  }, [invoices]);

  // Top 10 clients à risque
  const riskClients = useMemo(() => {
    const clientMap = new Map<number, any>();

    invoices.forEach(invoice => {
      if (invoice.statut_paiement === 'PAYEE') return;

      const clientId = invoice.client.id;
      if (!clientMap.has(clientId)) {
        clientMap.set(clientId, {
          id: clientId,
          name: invoice.client.raison_sociale,
          email: invoice.client.email,
          totalOutstanding: 0,
          overdueAmount: 0,
          maxDaysOverdue: 0,
          invoiceCount: 0,
          overdueCount: 0,
        });
      }

      const client = clientMap.get(clientId)!;
      const balance = invoice.total_ttc - invoice.montant_paye;

      client.totalOutstanding += balance;
      client.invoiceCount++;

      if (new Date(invoice.date_echeance) < new Date()) {
        client.overdueAmount += balance;
        client.overdueCount++;
        const days = Math.max(0, Math.floor((new Date().getTime() - new Date(invoice.date_echeance).getTime()) / (1000 * 3600 * 24)));
        client.maxDaysOverdue = Math.max(client.maxDaysOverdue, days);
      }
    });

    return Array.from(clientMap.values())
      .filter(c => c.totalOutstanding > 0)
      .map(c => ({
        ...c,
        riskScore: calculateRiskScore(c),
      }))
      .sort((a, b) => b.riskScore - a.riskScore)
      .slice(0, 10);
  }, [invoices]);

  // Prévisionnel des encaissements (12 mois)
  const cashFlowForecast = useMemo(() => {
    const forecast = [];
    const today = new Date();

    for (let i = 0; i < 12; i++) {
      const monthStart = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const monthEnd = new Date(today.getFullYear(), today.getMonth() + i + 1, 0);

      const monthlyDue = invoices
        .filter(inv => {
          if (inv.statut_paiement === 'PAYEE') return false;
          const dueDate = new Date(inv.date_echeance);
          return dueDate >= monthStart && dueDate <= monthEnd;
        })
        .reduce((sum, inv) => sum + (inv.total_ttc - inv.montant_paye), 0);

      forecast.push({
        month: monthStart.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }),
        optimistic: Math.round(monthlyDue * 0.95),
        realistic: Math.round(monthlyDue * 0.85),
        pessimistic: Math.round(monthlyDue * 0.70),
      });
    }

    return forecast;
  }, [invoices]);

  function calculateRiskScore(client: any): number {
    let score = 0;

    if (client.overdueAmount > 1000000) score += 40;
    else if (client.overdueAmount > 500000) score += 30;
    else if (client.overdueAmount > 200000) score += 20;
    else if (client.overdueAmount > 0) score += 10;

    if (client.maxDaysOverdue > 90) score += 30;
    else if (client.maxDaysOverdue > 60) score += 25;
    else if (client.maxDaysOverdue > 30) score += 20;
    else if (client.maxDaysOverdue > 0) score += 10;

    if (client.overdueCount > 5) score += 20;
    else if (client.overdueCount > 3) score += 15;
    else if (client.overdueCount > 1) score += 10;

    if (client.totalOutstanding > 2000000) score += 10;
    else if (client.totalOutstanding > 1000000) score += 7;
    else if (client.totalOutstanding > 500000) score += 5;

    return Math.min(score, 100);
  }

  function getRiskLevel(score: number) {
    if (score >= 80) return { label: 'Très élevé', color: 'bg-red-100 text-red-800' };
    if (score >= 60) return { label: 'Élevé', color: 'bg-orange-100 text-orange-800' };
    if (score >= 40) return { label: 'Moyen', color: 'bg-yellow-100 text-yellow-800' };
    if (score >= 20) return { label: 'Faible', color: 'bg-blue-100 text-blue-800' };
    return { label: 'Très faible', color: 'bg-green-100 text-green-800' };
  }

  const formatPrice = (amount: number) => amount.toLocaleString('fr-FR') + ' GNF';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-5xl text-blue-600"></i>
        <p className="ml-4 text-xl">Chargement du reporting crédit...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Synthèse de l'encours */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-8 py-6 border-b border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900">Synthèse de l'Encours Client</h3>
        </div>

        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600">{formatPrice(creditStats.totalOutstanding)}</div>
              <div className="text-gray-600 mt-2">Encours Total</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600">{formatPrice(creditStats.totalCurrent)}</div>
              <div className="text-gray-600 mt-2">Non Échu</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-red-600">{formatPrice(creditStats.totalOverdue)}</div>
              <div className="text-gray-600 mt-2">Échu ({creditStats.overdueCount} factures)</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600">{formatPrice(creditStats.totalDispute)}</div>
              <div className="text-gray-600 mt-2">En Litige ({creditStats.disputeCount})</div>
            </div>
          </div>

          {/* Répartition par ancienneté */}
          <div className="bg-gray-50 rounded-xl p-6">
            <h4 className="text-lg font-bold text-gray-900 mb-6">Répartition par Ancienneté du Retard</h4>
            <div className="space-y-5">
              {[
                { label: 'À jour', amount: creditStats.agingBuckets.current, color: 'bg-green-500' },
                { label: '1-30 jours', amount: creditStats.agingBuckets.days1to30, color: 'bg-yellow-500' },
                { label: '31-60 jours', amount: creditStats.agingBuckets.days31to60, color: 'bg-orange-500' },
                { label: '61-90 jours', amount: creditStats.agingBuckets.days61to90, color: 'bg-red-500' },
                { label: '> 90 jours', amount: creditStats.agingBuckets.over90, color: 'bg-red-700' },
              ].map((bucket) => {
                const percentage = creditStats.totalOutstanding > 0
                  ? (bucket.amount / creditStats.totalOutstanding) * 100
                  : 0;

                return (
                  <div key={bucket.label} className="flex items-center justify-between">
                    <span className="text-gray-700 font-medium">{bucket.label}</span>
                    <div className="flex items-center gap-4 w-64">
                      <div className="flex-1 bg-gray-200 rounded-full h-4">
                        <div
                          className={`${bucket.color} h-4 rounded-full transition-all duration-500`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-gray-900 font-bold w-32 text-right">
                        {formatPrice(bucket.amount)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Indicateurs clés */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
              <i className="ri-time-line text-3xl text-blue-600"></i>
            </div>
            <div>
              <p className="text-gray-600">Délai Moyen de Paiement</p>
              <p className="text-4xl font-bold text-gray-900 mt-2">{creditStats.averagePaymentDelay} jours</p>
              <p className="text-sm text-gray-500 mt-1">vs 30 jours contractuel</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 bg-red-100 rounded-xl flex items-center justify-center">
              <i className="ri-alarm-warning-line text-3xl text-red-600"></i>
            </div>
            <div>
              <p className="text-gray-600">Taux d'Impayés</p>
              <p className="text-4xl font-bold text-gray-900 mt-2">
                {creditStats.totalOutstanding > 0
                  ? ((creditStats.totalOverdue / creditStats.totalOutstanding) * 100).toFixed(1)
                  : 0}%
              </p>
              <p className="text-sm text-gray-500 mt-1">{creditStats.overdueCount} factures en retard</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center">
              <i className="ri-scales-3-line text-3xl text-purple-600"></i>
            </div>
            <div>
              <p className="text-gray-600">Taux de Litiges</p>
              <p className="text-4xl font-bold text-gray-900 mt-2">
                {creditStats.totalOutstanding > 0
                  ? ((creditStats.totalDispute / creditStats.totalOutstanding) * 100).toFixed(1)
                  : 0}%
              </p>
              <p className="text-sm text-gray-500 mt-1">{creditStats.disputeCount} factures</p>
            </div>
          </div>
        </div>
      </div>

      {/* Top 10 clients à risque */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-8 py-6 border-b border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900">Top 10 Clients à Risque</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-8 py-4 text-left text-sm font-bold text-gray-700 uppercase">Client</th>
                <th className="px-8 py-4 text-right text-sm font-bold text-gray-700 uppercase">Encours Total</th>
                <th className="px-8 py-4 text-right text-sm font-bold text-gray-700 uppercase">Impayé</th>
                <th className="px-8 py-4 text-center text-sm font-bold text-gray-700 uppercase">Retard Max</th>
                <th className="px-8 py-4 text-center text-sm font-bold text-gray-700 uppercase">Score de Risque</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {riskClients.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-gray-500">
                    Aucun client à risque détecté
                  </td>
                </tr>
              ) : (
                riskClients.map(client => {
                  const risk = getRiskLevel(client.riskScore);
                  return (
                    <tr key={client.id} className="hover:bg-gray-50 transition">
                      <td className="px-8 py-5">
                        <div className="font-medium text-gray-900">{client.name}</div>
                        <div className="text-sm text-gray-500">{client.email}</div>
                      </td>
                      <td className="px-8 py-5 text-right font-medium">{formatPrice(client.totalOutstanding)}</td>
                      <td className="px-8 py-5 text-right font-bold text-red-600">{formatPrice(client.overdueAmount)}</td>
                      <td className="px-8 py-5 text-center text-gray-900">{client.maxDaysOverdue} jours</td>
                      <td className="px-8 py-5 text-center">
                        <span className={`inline-flex px-4 py-2 rounded-full text-sm font-bold ${risk.color}`}>
                          {client.riskScore}/100 — {risk.label}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Prévisionnel des encaissements */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-8 py-6 border-b border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900">Prévisionnel des Encaissements (12 mois)</h3>
        </div>
        <div className="p-8">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="text-left py-4 text-lg font-bold text-gray-900">Mois</th>
                  <th className="text-right py-4 text-lg font-bold text-green-700">Optimiste (95%)</th>
                  <th className="text-right py-4 text-lg font-bold text-blue-700">Réaliste (85%)</th>
                  <th className="text-right py-4 text-lg font-bold text-red-700">Pessimiste (70%)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {cashFlowForecast.map((f, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="py-5 text-gray-900 font-medium">{f.month}</td>
                    <td className="py-5 text-right text-green-700 font-bold">{formatPrice(f.optimistic)}</td>
                    <td className="py-5 text-right text-blue-700 font-bold">{formatPrice(f.realistic)}</td>
                    <td className="py-5 text-right text-red-700 font-bold">{formatPrice(f.pessimistic)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 p-6 bg-blue-50 rounded-xl border border-blue-200">
            <h4 className="text-lg font-bold text-blue-900 mb-4">Recommandations pour optimiser le BFR</h4>
            <ul className="space-y-2 text-blue-800">
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-blue-600 mt-1"></i>
                <span>Réduire les délais de paiement pour les nouveaux clients</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-blue-600 mt-1"></i>
                <span>Automatiser les relances dès J+7</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-blue-600 mt-1"></i>
                <span>Négocier des acomptes sur les grosses commandes</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-blue-600 mt-1"></i>
                <span>Envisager l'affacturage pour les créances seperieurs a 60 jours</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}